/* FIDNO hero — the "color lives in the letters" signature.
 *
 * Three jobs, layered by capability:
 *   1. Registration sync (always, desktop only): size/position the photo-filled
 *      headline's background to the same cover geometry as the backdrop image,
 *      so the letters read as a full-color window cut through the mono photo.
 *      Pure presentation — it runs for reduced-motion visitors too.
 *   2. Load choreography (motion only): headline lines wipe up, the fill
 *      "develops" from mono to color, subline and CTA follow. ~1s total.
 *   3. Scroll drift (motion only): the fill scrubs a few px against the
 *      backdrop parallax, so the photo visibly moves inside the letterforms.
 *      This is a subtle scrub tied to normal scrolling, not a pin — it never
 *      fights or gates the visitor's scroll.
 *
 * The section ships `fidno-hero--armed` unconditionally in server-rendered
 * markup; it only hides content when html.fidno-motion is also present, and
 * that class is added synchronously in <head> before first paint (see
 * theme.liquid), so static/no-motion visitors never see hidden content. This
 * file disarms it when the entrance finishes — or immediately if GSAP never
 * showed up — so content is never left hidden for motion visitors either.
 */

(() => {
  'use strict';

  const DESKTOP = window.matchMedia('(min-width: 750px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const clipSupported =
    window.CSS && (CSS.supports('background-clip', 'text') || CSS.supports('-webkit-background-clip', 'text'));

  const motionActive = () =>
    document.documentElement.classList.contains('fidno-motion') && !reducedMotion.matches && window.gsap;

  const tokens = () =>
    (window.FIDNO && window.FIDNO.motion && window.FIDNO.motion.tokens) || {
      base: 0.35,
      slow: 0.65,
      easeOut: 'expo.out',
    };

  /* ---- 1. registration sync ------------------------------------------- */

  const FILL_VARS = ['--fidno-fill-size', '--fidno-fill-x', '--fidno-fill-y'];

  function syncFill(hero) {
    const headline = hero.querySelector('.fidno-hero__headline--photo-fill');
    const media = hero.querySelector('.fidno-hero__media');
    const img = hero.querySelector('.fidno-hero__img');
    if (!headline || !media || !img || !clipSupported) return;

    /* Mobile: headline sits in its own charcoal zone below the photo, so the
       plain element-cover fill from CSS is the right crop. */
    if (!DESKTOP.matches) {
      FILL_VARS.forEach((v) => headline.style.removeProperty(v));
      return;
    }
    if (!img.naturalWidth) return; // the load listener re-runs us

    const m = media.getBoundingClientRect();
    const h = headline.getBoundingClientRect();
    const ratio = img.naturalWidth / img.naturalHeight;

    /* Reproduce the backdrop's object-fit: cover math, including the parallax
       scale headroom, then express it relative to the headline's box. */
    const scale = document.documentElement.classList.contains('fidno-motion') ? 1.12 : 1;
    let dw;
    let dh;
    if (m.width / m.height > ratio) {
      dw = m.width;
      dh = m.width / ratio;
    } else {
      dh = m.height;
      dw = m.height * ratio;
    }
    dw *= scale;
    dh *= scale;

    headline.style.setProperty('--fidno-fill-size', `${dw.toFixed(1)}px ${dh.toFixed(1)}px`);
    headline.style.setProperty('--fidno-fill-x', `${(m.left - h.left + (m.width - dw) / 2).toFixed(1)}px`);
    headline.style.setProperty('--fidno-fill-y', `${(m.top - h.top + (m.height - dh) / 2).toFixed(1)}px`);
  }

  /* ---- 2. load choreography ------------------------------------------- */

  const disarm = (hero) => hero.classList.remove('fidno-hero--armed');

  function choreograph(hero) {
    if (!motionActive()) {
      disarm(hero);
      return;
    }
    const gsap = window.gsap;
    const t = tokens();
    const headline = hero.querySelector('.fidno-hero__headline');
    const lines = hero.querySelectorAll('.fidno-hero__line');
    const subline = hero.querySelector('.fidno-hero__subline');
    const cta = hero.querySelector('.fidno-hero__cta');
    const develop = hero.classList.contains('fidno-hero--aperture') && clipSupported;

    if (!headline || !lines.length) {
      disarm(hero);
      return;
    }

    try {
      const tail = [subline, cta].filter(Boolean);
      const tl = gsap.timeline({
        defaults: { ease: t.easeOut },
        /* clearProps happens here, not per-tween: while the hero is armed the
           CSS keeps lines/subline/CTA hidden, so inline values must persist
           until the whole entrance is over. */
        onComplete: () => {
          disarm(hero);
          gsap.set(lines, { clearProps: 'clipPath' });
          if (tail.length) gsap.set(tail, { clearProps: 'all' });
        },
      });

      tl.fromTo(headline, { y: 28 }, { y: 0, duration: t.slow, clearProps: 'transform' }, 0);
      /* clip-path wipe only — transforms on descendants of a background-clip:
         text element break the fill, so the lines never translate themselves. */
      tl.fromTo(
        lines,
        { clipPath: 'inset(100% 0 0 0)' },
        { clipPath: 'inset(0% 0 0 0)', duration: t.slow, stagger: 0.12 },
        0.05
      );
      if (develop) {
        tl.fromTo(
          headline,
          { filter: 'grayscale(1) brightness(0.65)' },
          { filter: 'grayscale(0) brightness(1)', duration: 0.55, clearProps: 'filter' },
          0.3
        );
      }
      if (subline) tl.fromTo(subline, { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: t.base }, 0.55);
      if (cta) tl.fromTo(cta, { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: t.base }, 0.65);
    } catch (error) {
      disarm(hero);
      throw error;
    }
  }

  /* ---- 3. scroll drift -------------------------------------------------- */

  function initDrift(hero) {
    if (!motionActive() || !window.ScrollTrigger) return;
    if (!hero.classList.contains('fidno-hero--aperture') || !clipSupported) return;
    const headline = hero.querySelector('.fidno-hero__headline--photo-fill');
    if (!headline || headline.dataset.fidnoDriftInit) return;
    headline.dataset.fidnoDriftInit = '1';

    window.gsap.registerPlugin(window.ScrollTrigger);
    /* Opposite direction to the backdrop parallax: the letters stay a fixed
       window while the world moves behind them. Same trigger range as
       [data-fidno-parallax] so the two motions read as one system. */
    window.gsap.fromTo(
      headline,
      { '--fidno-fill-drift': '24px' },
      {
        '--fidno-fill-drift': '-24px',
        ease: 'none',
        scrollTrigger: { trigger: hero, start: 'top bottom', end: 'bottom top', scrub: true },
      }
    );
  }

  /* ---- wiring ----------------------------------------------------------- */

  const debounce = (fn, ms) => {
    let id;
    return () => {
      clearTimeout(id);
      id = setTimeout(fn, ms);
    };
  };

  function initHero(hero) {
    if (hero.dataset.fidnoHeroInit) return;
    hero.dataset.fidnoHeroInit = '1';

    syncFill(hero);

    const img = hero.querySelector('.fidno-hero__img');
    if (img && !img.complete) img.addEventListener('load', () => syncFill(hero), { once: true });
    /* Anton swapping in changes the headline's box — re-measure. */
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => syncFill(hero));

    window.addEventListener('resize', debounce(() => syncFill(hero), 150));
    DESKTOP.addEventListener('change', () => syncFill(hero));

    choreograph(hero);
    initDrift(hero);
  }

  const initAll = (scope) => (scope || document).querySelectorAll('[data-fidno-hero]').forEach(initHero);

  /* If the visitor switches to reduced motion mid-entrance, surface everything. */
  reducedMotion.addEventListener('change', () => {
    if (!reducedMotion.matches) return;
    document.querySelectorAll('[data-fidno-hero]').forEach((hero) => {
      disarm(hero);
      if (!window.gsap) return;
      const els = hero.querySelectorAll('.fidno-hero__headline, .fidno-hero__line, .fidno-hero__subline, .fidno-hero__cta');
      window.gsap.killTweensOf(els);
      window.gsap.set(els, { clearProps: 'all' });
    });
  });

  initAll(document);
  /* Theme editor: sections re-render in place. */
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
